import { useState } from "react";
import { DonationCampaign } from "@/mocks/community";

interface Props {
  campaign?: DonationCampaign | null;
  onClose: () => void;
}

const presets = [10, 25, 50, 100, 250, 500];

export default function DonationModal({ campaign, onClose }: Props) {
  const [amount, setAmount] = useState<number | "">("");
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [step, setStep] = useState<"form" | "success">("form");

  const finalAmount = amount !== "" ? amount : custom ? parseFloat(custom) : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) return;
    const body = new URLSearchParams({
      campaign: campaign?.title || "General Infant Fund",
      amount: String(finalAmount),
      name: anonymous ? "Anonymous" : name,
      email,
      message,
    });
    try {
      await fetch("https://readdy.ai/api/form/d7cn5rckpdh0a4advm10", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch (_e) { /* silent */ }
    setStep("success");
  };

  const pct = campaign ? Math.min(100, Math.round((campaign.raised / campaign.goal) * 100)) : 72;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {step === "success" ? (
          <div className="flex flex-col items-center py-16 px-8 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-rose-100 rounded-full mb-5">
              <i className="ri-heart-fill text-rose-500 text-4xl"></i>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Thank You! 💝</h2>
            <p className="text-gray-500 text-sm mb-2">Your donation of <strong className="text-rose-500">${finalAmount}</strong> has been received.</p>
            <p className="text-gray-400 text-sm mb-8">You are making a real difference in the lives of infants around the world. Together we can give every child a chance to thrive. 🌍</p>
            <button onClick={onClose} className="bg-rose-500 hover:bg-rose-600 text-white font-bold px-10 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">Close</button>
          </div>
        ) : (
          <>
            {/* Campaign header */}
            <div className="relative">
              <div className="w-full h-44 overflow-hidden rounded-t-2xl">
                <img
                  src={campaign?.image || "https://public.readdy.ai/ai/img_res/c767e8f144810614829ec252aad193a0.jpg"}
                  alt={campaign?.title || "World Infant Fund"}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent rounded-t-2xl"></div>
              </div>
              <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
              <div className="absolute bottom-4 left-5 right-5">
                {campaign?.urgent && <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-0.5 rounded-full mb-2 inline-block">URGENT</span>}
                <h2 className="text-white font-extrabold text-lg leading-snug">{campaign?.title || "World Infant Fund — Love, Milk & Clothes"}</h2>
                <p className="text-white/70 text-xs mt-0.5">{campaign ? `${campaign.flag} ${campaign.country}` : "🌍 30+ Countries · CrownGlobal Cares"}</p>
              </div>
            </div>

            <div className="p-6">
              {/* Progress */}
              <div className="mb-5">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-extrabold text-gray-900">${(campaign?.raised || 284500).toLocaleString()} raised</span>
                  <span className="text-gray-400">of ${(campaign?.goal || 500000).toLocaleString()} goal</span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-rose-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }}></div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-400 mt-1.5">
                  <span>{pct}% funded</span>
                  <span>{(campaign?.donors || 12840).toLocaleString()} donors</span>
                </div>
              </div>

              <p className="text-gray-500 text-sm leading-relaxed mb-6">{campaign?.description || "When sellers complete container orders on CrownGlobal, we welcome donations for infants around the world who need love, milk, and clothes. Every purchase you make helps a child thrive. Together, we can make a difference."}</p>

              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* Amount presets */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-2 block">Choose Donation Amount (USD) *</label>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {presets.map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => { setAmount(p); setCustom(""); }}
                        className={`py-2.5 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${amount === p ? "bg-rose-500 text-white" : "bg-gray-50 text-gray-700 hover:bg-rose-50 hover:text-rose-500 border border-gray-200"}`}
                      >
                        ${p}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    min="1"
                    value={custom}
                    onChange={e => { setCustom(e.target.value); setAmount(""); }}
                    placeholder="Or enter custom amount..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-400 transition-colors"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input type="checkbox" id="anon" checked={anonymous} onChange={e => setAnonymous(e.target.checked)} className="w-4 h-4 accent-rose-500 cursor-pointer" />
                  <label htmlFor="anon" className="text-sm text-gray-600 cursor-pointer">Donate anonymously</label>
                </div>

                {!anonymous && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Your Name</label>
                      <input name="name" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-400 transition-colors" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Email *</label>
                      <input name="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-400 transition-colors" />
                    </div>
                  </div>
                )}
                {anonymous && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Email (for receipt) *</label>
                    <input name="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-400 transition-colors" />
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Message of Hope (optional)</label>
                  <textarea
                    name="message"
                    value={message}
                    onChange={e => { if (e.target.value.length <= 200) setMessage(e.target.value); }}
                    placeholder="Leave a message of love and hope for these infants..."
                    rows={2}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-400 transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!finalAmount || finalAmount <= 0}
                  className="bg-rose-500 hover:bg-rose-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-extrabold py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base"
                >
                  <i className="ri-heart-fill mr-2"></i>
                  Donate {finalAmount ? `$${finalAmount}` : ""} Now
                </button>
                <p className="text-center text-xs text-gray-400">100% of your donation goes directly to infants in need. Secure & encrypted.</p>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
